import { moveToFulfilled, update } from '@ms/yammer-data-support';
import { Reducer } from 'redux';

import {
  FeedLoadAction,
  FeedLoadNewerThreadsFulfilledAction,
  FeedLoadOlderThreadsFulfilledAction,
} from '../../domains/feedLoad/actions.types';
import {
  UserNetworkLeadersListLoadFulfilledAction,
  UserNetworkLeadersListLoadMoreFulfilledAction,
} from '../../domains/leadersManagement/actions.types';
import { NestedThreadLoadFulfilledAction } from '../../domains/nestedThread/types';
import { NestedThreadListLoadFulfilledAction } from '../../domains/nestedThreadList/actions.types';
import { MarkAllNotificationsAsSeenFulfilledAction } from '../../domains/notificationFeeds/actions.types';
import { NotificationLoadMoreFulfilledAction } from '../../domains/notificationPaginations/actions.types';
import { SuggestedActionsLoadFulfilledAction } from '../../domains/suggestedActions/actions.types';
import { SuggestedLeadersLoadAction } from '../../domains/suggestedLeaders/loadActions.types';
import { SuggestedLeadersLoadMoreAction } from '../../domains/suggestedLeaders/loadMoreActions.types';
import { SuggestedUsersLoadAction } from '../../domains/suggestedUsers/loadActions.types';
import { SuggestedUsersLoadMoreAction } from '../../domains/suggestedUsers/loadMoreActions.types';
import { ThreadExtendedActivityTopRepliesLoadFulfilledAction } from '../../domains/threadExtendedActivityTopReplies/actions.types';
import { UserFollowAction } from '../../domains/userFollow/followActions.types';
import { UserUnfollowAction } from '../../domains/userFollow/unfollowActions.types';
import { UserFollowedUsersLoadAction } from '../../domains/userFollowedUsers/actions.types';
import { UserHeaderLoadAction } from '../../domains/userHeaderLoad/actions.types';
import { UserInsightsInfluenceLoadFulfilledAction } from '../../domains/userInsightsInfluences/actions.types';
import { ViewerIsFollowingUserLoadFulfilledAction } from '../../domains/viewerIsFollowingUsers/actions.types';
import { UserFollowedByLoadAction } from '../userFollowedBy/reducer';

import { ViewerIsFollowingUsers } from './types';
export type ViewerIsFollowingUsersAction =
  | FeedLoadAction
  | FeedLoadNewerThreadsFulfilledAction
  | FeedLoadOlderThreadsFulfilledAction
  | UserHeaderLoadAction
  | UserFollowAction
  | UserUnfollowAction
  | UserFollowedUsersLoadAction
  | UserInsightsInfluenceLoadFulfilledAction
  | UserFollowedByLoadAction
  | MarkAllNotificationsAsSeenFulfilledAction
  | NotificationLoadMoreFulfilledAction
  | NestedThreadLoadFulfilledAction
  | NestedThreadListLoadFulfilledAction
  | SuggestedActionsLoadFulfilledAction
  | SuggestedLeadersLoadAction
  | SuggestedLeadersLoadMoreAction
  | SuggestedUsersLoadAction
  | SuggestedUsersLoadMoreAction
  | ThreadExtendedActivityTopRepliesLoadFulfilledAction
  | ViewerIsFollowingUserLoadFulfilledAction
  | UserNetworkLeadersListLoadFulfilledAction
  | UserNetworkLeadersListLoadMoreFulfilledAction;

const initialState: ViewerIsFollowingUsers = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const reducer: Reducer<ViewerIsFollowingUsers, ViewerIsFollowingUsersAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'FEED.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'FEED.LOAD_NEWER_THREADS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'FEED.LOAD_OLDER_THREADS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'MARK_ALL_NOTIFICATIONS_AS_SEEN.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'NESTED_THREAD.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'NESTED_THREAD_LIST.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'NOTIFICATION.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'USER_FOLLOWED_BY.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'USER_FOLLOWED_USERS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'USER_HEADER.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'USER_INSIGHTS_INFLUENCE.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'SUGGESTED_ACTION.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'SUGGESTED_LEADERS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'SUGGESTED_LEADERS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'SUGGESTED_USERS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'SUGGESTED_USERS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'VIEWER_IS_FOLLOWING_USER.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'THREAD_EXTENDED_ACTIVITY_TOP_REPLIES.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'USER.FOLLOW_FULFILLED':
      return update(state, action.payload.id, { isFollowing: true });
    case 'USER.UNFOLLOW_FULFILLED':
      return update(state, action.payload.id, { isFollowing: false });
    case 'USER.FOLLOW_PENDING':
      return update(state, action.payload.id, { isFollowing: true });
    case 'USER.UNFOLLOW_REJECTED':
      return update(state, action.payload.id, { isFollowing: true });
    case 'USER.UNFOLLOW_PENDING':
      return update(state, action.payload.id, { isFollowing: false });
    case 'USER.FOLLOW_REJECTED':
      return update(state, action.payload.id, { isFollowing: false });
    case 'ALL_LEADERS_IN_USERS_NETWORK.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    case 'ALL_LEADERS_IN_USERS_NETWORK.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerIsFollowingUsers);
    default:
      return state;
  }
};

export default reducer;
